import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from './AuthContext'

const UserContext = createContext()

export const useUser = () => {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUser must be used within a UserContext')
  }
  return context
}

export const UserProvider = ({ children }) => {
  const { user } = useAuth()
  const [favorites, setFavorites] = useState([])
  const [dailyDrawHistory, setDailyDrawHistory] = useState([])
  const [allUsers, setAllUsers] = useState([])

  // Load user data from localStorage
  useEffect(() => {
    if (user) {
      const storedFavorites = localStorage.getItem(`tarotPairs_favorites_${user.id}`)
      if (storedFavorites) {
        try {
          setFavorites(JSON.parse(storedFavorites))
        } catch (error) {
          console.error('Error loading favorites:', error)
        }
      }

      const storedDraws = localStorage.getItem(`tarotPairs_draws_${user.id}`)
      if (storedDraws) {
        try {
          setDailyDrawHistory(JSON.parse(storedDraws))
        } catch (error) {
          console.error('Error loading daily draws:', error)
        }
      }
    } else {
      setFavorites([])
      setDailyDrawHistory([])
    }
  }, [user])

  // Load mock users for admin
  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'moderator')) {
      const mockUsers = [
        {
          id: 'user-1',
          name: 'Alice Johnson',
          email: 'alice@example.com',
          role: 'user',
          isPremium: false,
          status: 'active',
          joinDate: new Date('2024-01-15').toISOString(),
          lastActive: new Date().toISOString()
        },
        {
          id: 'user-2',
          name: 'Bob Smith',
          email: 'bob@example.com',
          role: 'premium',
          isPremium: true,
          status: 'active',
          joinDate: new Date('2024-02-20').toISOString(),
          lastActive: new Date().toISOString()
        },
        {
          id: 'user-3',
          name: 'Carol Davis',
          email: 'carol@example.com',
          role: 'moderator',
          isPremium: true,
          status: 'active',
          joinDate: new Date('2024-01-10').toISOString(),
          lastActive: new Date().toISOString()
        }
      ]
      setAllUsers([user, ...mockUsers])
    }
  }, [user])

  const addToFavorites = async (cardPair) => {
    if (!user) return

    try {
      const newFavorite = {
        id: `fav-${Date.now()}`,
        ...cardPair,
        dateAdded: new Date().toISOString()
      }

      const updatedFavorites = [newFavorite, ...favorites]
      setFavorites(updatedFavorites)
      localStorage.setItem(`tarotPairs_favorites_${user.id}`, JSON.stringify(updatedFavorites))
    } catch (error) {
      console.error('Error adding to favorites:', error)
    }
  }

  const removeFromFavorites = async (favoriteId) => {
    if (!user) return

    try {
      const updatedFavorites = favorites.filter(fav => fav.id !== favoriteId)
      setFavorites(updatedFavorites)
      localStorage.setItem(`tarotPairs_favorites_${user.id}`, JSON.stringify(updatedFavorites))
    } catch (error) {
      console.error('Error removing favorite:', error)
    }
  }

  const addToDailyDrawHistory = async (draw) => {
    if (!user) return

    try {
      const newDraw = {
        id: `draw-${Date.now()}`,
        ...draw,
        date: new Date().toISOString()
      }

      const updatedDraws = [newDraw, ...dailyDrawHistory.slice(0, 29)] // Keep last 30
      setDailyDrawHistory(updatedDraws)
      localStorage.setItem(`tarotPairs_draws_${user.id}`, JSON.stringify(updatedDraws))
    } catch (error) {
      console.error('Error adding to daily draw history:', error)
    }
  }

  // Mock user management functions for admin
  const getAllUsers = () => {
    return allUsers
  }

  const addUser = async (userData) => {
    const newUser = {
      id: 'user-' + Date.now(),
      ...userData,
      joinDate: new Date().toISOString(),
      lastActive: new Date().toISOString()
    }
    setAllUsers(prev => [newUser, ...prev])
    return newUser
  }

  const updateUser = async (userId, updates) => {
    setAllUsers(prev => prev.map(u => u.id === userId ? { ...u, ...updates } : u))
  }

  const deleteUser = async (userId) => {
    setAllUsers(prev => prev.filter(u => u.id !== userId))
  }

  const getUserByEmail = (email) => {
    return allUsers.find(u => u.email === email)
  }

  const value = {
    favorites,
    dailyDrawHistory,
    addToFavorites,
    removeFromFavorites,
    addToDailyDrawHistory,
    // User management
    allUsers,
    getAllUsers,
    addUser,
    updateUser,
    deleteUser,
    getUserByEmail,
    loadUserData: () => {}, // Placeholder
    loadAllUsers: () => {} // Placeholder
  }

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  )
}